import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, BarChart3, Bell, Briefcase, CalendarOff, ClipboardList, Coffee, Download, FileText,
  History, Mail, MapPin, Scale, Shield, Users,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, fmtHoras, fmtHora, hoyISO, inicioMesISO, ROL_LABEL } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useEmpresa } from "@/hooks/useEmpresa";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "@/components/PageHeader";
import MapaFichajeDialog from "@/components/MapaFichajeDialog";
import FichajeTrazabilidadDialog from "@/components/FichajeTrazabilidadDialog";
import ListadoCabecera from "@/components/ListadoCabecera";
import BalanceHorasCards from "@/components/BalanceHorasCards";
import CompensacionSaldoPanel from "@/components/CompensacionSaldoPanel";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { sectionGradientStyle, informeSectionKey, kpiSectionKey } from "@/lib/sectionTheme";

function badgeEstadoAusencia(a) {
  const est = a.estado;
  const className = {
    aprobada: "border-green-200 bg-green-50 text-green-800",
    aprobada_parcial: "border-teal-200 bg-teal-50 text-teal-800",
    rechazada: "border-red-200 bg-red-50 text-red-800",
    pendiente_trabajador: "border-orange-200 bg-orange-50 text-orange-900",
    pendiente: "border-amber-200 bg-amber-50 text-amber-800",
  }[est] || "border-zinc-200 bg-zinc-50 text-zinc-600";
  return { label: a.estado_label || est || "—", className };
}

function AlcanceAusencia({ a }) {
  const parcial = a.alcance === "parcial";
  return (
    <div>
      <Badge
        variant="outline"
        className={parcial ? "border-amber-200 bg-amber-50 text-amber-900" : "border-zinc-200 bg-zinc-50 text-zinc-700"}
      >
        {a.alcance_label || (parcial ? "Parcial" : "Día completo")}
      </Badge>
      {parcial && (
        <p className="mt-1 text-xs font-medium text-zinc-600" data-testid="ausencia-tramo">
          {a.tramo || "Tramo sin determinar"}
        </p>
      )}
    </div>
  );
}

const TIPOS = [
  {
    id: "dashboard",
    label: "Cuadro de mando",
    desc: "Totales, saldos, horas extra y absentismo",
    perfiles: ["admin"],
    icon: BarChart3,
    color: "from-indigo-500 to-violet-600",
  },
  {
    id: "balance",
    label: "Balance de horas",
    desc: "Horas trabajadas, previstas y bolsa por empleado",
    perfiles: ["admin", "manager", "worker"],
    icon: Users,
    color: "from-emerald-500 to-teal-600",
  },
  {
    id: "fichajes",
    label: "Histórico de fichajes",
    desc: "Entradas, salidas, pausas e incidencias con GPS",
    perfiles: ["admin", "manager", "worker"],
    icon: History,
    color: "from-sky-500 to-blue-600",
  },
  {
    id: "picados_proyectos",
    label: "Picados por proyecto",
    desc: "Horas fichadas en proyectos: código, día, entrada y salida",
    perfiles: ["admin", "manager", "worker"],
    requiereProyectos: true,
    icon: Briefcase,
    color: "from-violet-500 to-purple-600",
  },
  {
    id: "incidencias",
    label: "Incidencias",
    desc: "Fichajes manuales, sesiones abiertas y alertas",
    perfiles: ["admin", "manager", "worker"],
    icon: AlertTriangle,
    color: "from-amber-500 to-orange-600",
  },
  {
    id: "ausencias",
    label: "Ausencias",
    desc: "Vacaciones, bajas y ausencias justificadas (aprobadas y rechazadas)",
    perfiles: ["admin", "manager", "worker"],
    icon: CalendarOff,
    color: "from-rose-500 to-pink-600",
  },
  {
    id: "registro_jornada",
    label: "Registro de jornada",
    desc: "Art. 34.9 ET · inicio y fin diarios · exportación ITSS",
    perfiles: ["admin", "manager", "worker"],
    icon: Shield,
    color: "from-slate-600 to-zinc-700",
  },
  {
    id: "analisis_pausas",
    label: "Análisis de pausas",
    desc: "Tiempo en pausa por año, departamento, centro y comparativa interanual",
    perfiles: ["admin", "manager"],
    icon: Coffee,
    color: "from-amber-500 to-orange-600",
    ruta: "/admin/analisis-pausas",
  },
  {
    id: "compensaciones",
    label: "Compensaciones",
    desc: "Horas extra compensadas con tiempo libre",
    perfiles: ["admin", "manager", "worker"],
    icon: Scale,
    color: "from-violet-500 to-purple-600",
  },
];

function rangoRapido(tipo) {
  const hoy = new Date();
  const iso = (d) => d.toLocaleDateString("sv");
  if (tipo === "hoy") return [iso(hoy), iso(hoy)];
  if (tipo === "semana") {
    const lunes = new Date(hoy);
    lunes.setDate(hoy.getDate() - ((hoy.getDay() + 6) % 7));
    return [iso(lunes), iso(hoy)];
  }
  if (tipo === "mes") return [inicioMesISO(), hoyISO()];
  if (tipo === "mes_anterior") {
    const ini = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    const fin = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
    return [iso(ini), iso(fin)];
  }
  return [inicioMesISO(), hoyISO()];
}

function KpiCard({ label, value, sectionKey, onClick, title }) {
  const cl = `rounded-xl p-4 text-white shadow-md text-left w-full${
    onClick ? " cursor-pointer transition hover:shadow-lg hover:brightness-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50" : ""
  }`;
  const inner = (
    <>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-white/80">{label}</p>
      <p className="font-display mt-1 text-2xl font-light">{value}</p>
    </>
  );
  const style = sectionGradientStyle(sectionKey, "to bottom right");
  if (onClick) {
    return (
      <button type="button" onClick={onClick} title={title || `Ver detalle: ${label}`} className={cl} style={style}>
        {inner}
      </button>
    );
  }
  return <div className={cl} style={style}>{inner}</div>;
}

function resumenRegistradoFichaje(f) {
  if (f.registrado_por) return f.registrado_por;
  if (f.origen === "solicitud_aprobada" && f.solicitud_id) return `Vía solicitud #${f.solicitud_id}`;
  if (f.justificacion?.toLowerCase().includes("cierre automático") || f.justificacion?.toLowerCase().includes("cierre automatico")) {
    return "Sistema (autocierre)";
  }
  return "—";
}

export default function Informes() {
  const { user } = useAuth();
  const { empresa } = useEmpresa();
  const navigate = useNavigate();
  const esAdmin = user?.rol === "admin";
  const esManager = user?.rol === "manager";
  const esWorker = user?.rol === "worker";

  const tiposVisibles = TIPOS.filter(
    (t) => t.perfiles.includes(user?.rol) && (!t.requiereProyectos || empresa?.gestionar_proyectos),
  );
  const [tipo, setTipo] = useState(tiposVisibles[0]?.id || "balance");

  const elegirTipo = (t) => {
    if (t.ruta) navigate(t.ruta);
    else setTipo(t.id);
  };

  const [desde, setDesde] = useState(inicioMesISO());
  const [hasta, setHasta] = useState(hoyISO());
  const [filtroUsuario, setFiltroUsuario] = useState("all");
  const [filtroDep, setFiltroDep] = useState("all");
  const [filtroCentro, setFiltroCentro] = useState("all");

  const [usuarios, setUsuarios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [centros, setCentros] = useState([]);

  const [resumen, setResumen] = useState(null);
  const [registroJornada, setRegistroJornada] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [fichajes, setFichajes] = useState([]);
  const [picadosProyectos, setPicadosProyectos] = useState(null);
  const [incidencias, setIncidencias] = useState([]);
  const [ausencias, setAusencias] = useState([]);
  const [compensaciones, setCompensaciones] = useState([]);
  const [compOpen, setCompOpen] = useState(false);
  const [compForm, setCompForm] = useState({
    usuario_id: "",
    horas: "1",
    periodo_desde: inicioMesISO(),
    periodo_hasta: hoyISO(),
    fecha_disfrute_inicio: hoyISO(),
    fecha_disfrute_fin: hoyISO(),
    motivo: "",
    tipo_compensacion: "tiempo_libre",
    modalidad: "periodo",
    importe_euros: "",
  });
  const [guardandoComp, setGuardandoComp] = useState(false);
  const [compCalculo, setCompCalculo] = useState(null);
  const [extraPendOpen, setExtraPendOpen] = useState(false);
  const [extraPendItems, setExtraPendItems] = useState([]);
  const [extraPendLoading, setExtraPendLoading] = useState(false);
  const [pendRevision, setPendRevision] = useState([]);
  const [cabecera, setCabecera] = useState({ empresa: null, trabajador: null });
  const [mapaFichaje, setMapaFichaje] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDest, setEmailDest] = useState(user?.email || "");
  const [emailFormato, setEmailFormato] = useState("csv");
  const [enviandoEmail, setEnviandoEmail] = useState(false);

  const [detalleOpen, setDetalleOpen] = useState(false);
  const [detalleTitulo, setDetalleTitulo] = useState("");
  const [detalleModo, setDetalleModo] = useState(null);
  const [detalleItems, setDetalleItems] = useState([]);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [notifAusencia, setNotifAusencia] = useState(null);
  const [notifMensaje, setNotifMensaje] = useState("");
  const [notifEmail, setNotifEmail] = useState(true);
  const [notifEnviando, setNotifEnviando] = useState(false);
  const [trazabilidadFichaje, setTrazabilidadFichaje] = useState(null);

  const params = useMemo(() => {
    const p = { desde, hasta };
    if (filtroUsuario !== "all") p.usuario_id = Number(filtroUsuario);
    if (filtroDep !== "all") p.departamento_id = Number(filtroDep);
    if (filtroCentro !== "all") p.centro_id = Number(filtroCentro);
    return p;
  }, [desde, hasta, filtroUsuario, filtroDep, filtroCentro]);

  const cargarMeta = useCallback(async () => {
    try {
      if (esWorker) {
        setUsuarios([]);
        setDepartamentos([]);
        setCentros([]);
        return;
      }
      const reqs = [api.get("/usuarios")];
      if (esAdmin || esManager) {
        reqs.push(api.get("/departamentos"));
        if (esAdmin) reqs.push(api.get("/centros"));
      }
      const results = await Promise.all(reqs);
      setUsuarios(results[0].data);
      if (results[1]) setDepartamentos(results[1].data);
      if (results[2]) setCentros(results[2].data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [esAdmin, esManager, esWorker]);

  const cargar = useCallback(async () => {
    try {
      if (tipo === "dashboard") {
        const { data } = await api.get("/informes/kpis", { params });
        setKpis(data);
        const r = await api.get("/informes/resumen", { params });
        setResumen(r.data);
        setCabecera({ empresa: r.data.empresa, trabajador: r.data.trabajador });
      } else if (tipo === "balance") {
        const { data } = await api.get("/informes/resumen", { params });
        setResumen(data);
        setCabecera({ empresa: data.empresa, trabajador: data.trabajador });
      } else if (tipo === "registro_jornada") {
        const { data } = await api.get("/informes/registro-jornada", { params });
        setRegistroJornada(data);
        setCabecera({ empresa: data.empresa, trabajador: data.trabajador });
      } else if (tipo === "fichajes") {
        const { data } = await api.get("/informes/fichajes", { params });
        setFichajes(data.items || []);
        setCabecera({ empresa: data.empresa, trabajador: data.trabajador });
      } else if (tipo === "picados_proyectos") {
        const { data } = await api.get("/informes/picados-proyectos", { params });
        setPicadosProyectos(data);
        setCabecera({ empresa: data.empresa, trabajador: data.trabajador });
      } else if (tipo === "incidencias") {
        const { data } = await api.get("/informes/incidencias", { params });
        setIncidencias(data.items || []);
        setCabecera({ empresa: data.empresa, trabajador: data.trabajador });
      } else if (tipo === "ausencias") {
        const { data } = await api.get("/informes/ausencias", { params });
        setAusencias(data.items || []);
        setCabecera({ empresa: data.empresa, trabajador: data.trabajador });
      } else if (tipo === "compensaciones") {
        const reqs = [
          api.get("/informes/resumen", { params }),
          api.get("/compensaciones", { params }),
        ];
        if (esAdmin || esManager) reqs.push(api.get("/compensaciones/pendientes-revision"));
        const results = await Promise.all(reqs);
        setResumen(results[0].data);
        setCompensaciones(results[1].data.items || []);
        setPendRevision(results[2]?.data || []);
        setCabecera({ empresa: results[0].data.empresa, trabajador: results[0].data.trabajador });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [tipo, params, esAdmin, esManager]);

  useEffect(() => { cargarMeta(); }, [cargarMeta]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    if (tiposVisibles.length && !tiposVisibles.some((t) => t.id === tipo)) {
      setTipo(tiposVisibles[0].id);
    }
  }, [tiposVisibles, tipo]);
  useEffect(() => { if (user?.email) setEmailDest(user.email); }, [user?.email]);

  const ambitoTexto = useMemo(() => {
    if (esWorker) return "Vista personal: solo tus fichajes, horas y ausencias.";
    if (esManager) {
      const dep = user?.departamento || "tu departamento";
      return `Vista de encargado: datos agregados de ${dep}.`;
    }
    return "Vista global: todos los centros, departamentos y empleados de la empresa.";
  }, [esWorker, esManager, user?.departamento]);

  const depsFiltrados = useMemo(() => {
    if (filtroCentro === "all") return departamentos;
    return departamentos.filter((d) => d.centro_id === Number(filtroCentro));
  }, [departamentos, filtroCentro]);

  const exportarPicadosProyectos = async () => {
    try {
      const res = await api.get("/informes/picados-proyectos/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `picados_proyectos_${desde}_${hasta}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Listado exportado a Excel");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportar = async (formato) => {
    try {
      let endpoint;
      let prefijo;
      let extraParams = {};
      if (tipo === "registro_jornada") {
        endpoint = formato === "pdf" ? "/informes/registro-jornada/export-pdf" : "/informes/registro-jornada/export";
        prefijo = "registro_jornada";
      } else if (VISTAS_EXPORT_PROPIO.includes(tipo)) {
        endpoint = formato === "pdf" ? "/informes/vista/export-pdf" : "/informes/vista/export";
        prefijo = tipo;
        extraParams = { vista: tipo };
      } else {
        endpoint = formato === "pdf" ? "/informes/export-pdf" : "/informes/export";
        prefijo = "listado";
      }
      const res = await api.get(endpoint, { params: { ...params, ...extraParams }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${prefijo}_${desde}_${hasta}.${formato === "pdf" ? "pdf" : "csv"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Listado descargado");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirCompensacion = (u = null) => {
    const anioIni = `${new Date().getFullYear()}-01-01`;
    setCompForm({
      usuario_id: u ? String(u.usuario_id) : (filtroUsuario !== "all" ? filtroUsuario : ""),
      horas: u?.extra_pendiente_segundos
        ? String(Math.max(0.25, Math.round((u.extra_pendiente_segundos / 3600) * 4) / 4))
        : "1",
      periodo_desde: desde || anioIni,
      periodo_hasta: hasta || hoyISO(),
      fecha_disfrute_inicio: hoyISO(),
      fecha_disfrute_fin: hoyISO(),
      motivo: "Compensación de horas extra",
      tipo_compensacion: "tiempo_libre",
      modalidad: "periodo",
      importe_euros: "",
    });
    setCompOpen(true);
  };

  const abrirExtraPendientes = async () => {
    setExtraPendOpen(true);
    setExtraPendLoading(true);
    setExtraPendItems([]);
    try {
      const { data } = await api.get("/informes/extra-pendientes", { params });
      setExtraPendItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setExtraPendOpen(false);
    } finally {
      setExtraPendLoading(false);
    }
  };

  const revisarCompensacion = async (c, aprobada) => {
    try {
      await api.put(`/compensaciones/${c.id}/revisar`, { aprobada, comentario: "" });
      toast.success(aprobada ? "Solicitud aprobada" : "Solicitud rechazada");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cerrarDetalle = () => {
    setDetalleOpen(false);
    setNotifAusencia(null);
    setNotifMensaje("");
  };

  const abrirDetalleFichajes = async (usuarioId, titulo) => {
    setDetalleOpen(true);
    setDetalleTitulo(titulo);
    setDetalleModo("fichajes");
    setDetalleItems([]);
    setNotifAusencia(null);
    setDetalleLoading(true);
    try {
      const { data } = await api.get("/informes/fichajes", {
        params: { ...params, usuario_id: usuarioId },
      });
      setDetalleItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setDetalleOpen(false);
    } finally {
      setDetalleLoading(false);
    }
  };

  const abrirDetalleManuales = async (usuarioId = null, nombre = null) => {
    setDetalleOpen(true);
    setDetalleTitulo(nombre ? `Fichajes manuales — ${nombre}` : "Fichajes manuales");
    setDetalleModo("manuales");
    setDetalleItems([]);
    setNotifAusencia(null);
    setDetalleLoading(true);
    try {
      const p = { ...params, solo_manuales: true };
      if (usuarioId) p.usuario_id = usuarioId;
      const { data } = await api.get("/informes/fichajes", { params: p });
      setDetalleItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setDetalleOpen(false);
    } finally {
      setDetalleLoading(false);
    }
  };

  const abrirDetalleAusencias = async (usuarioId = null, nombre = null) => {
    setDetalleOpen(true);
    setDetalleTitulo(nombre ? `Ausencias — ${nombre}` : "Días de ausencia");
    setDetalleModo("ausencias");
    setDetalleItems([]);
    setNotifAusencia(null);
    setDetalleLoading(true);
    try {
      const p = { ...params };
      if (usuarioId) p.usuario_id = usuarioId;
      const { data } = await api.get("/informes/ausencias", { params: p });
      setDetalleItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setDetalleOpen(false);
    } finally {
      setDetalleLoading(false);
    }
  };

  const abrirNotificarAusencia = (item) => {
    setNotifAusencia(item);
    setNotifMensaje(
      `Revisa tu ausencia (${item.tipo_label}) del ${fmtFecha(item.fecha_inicio)} al ${fmtFecha(item.fecha_fin)}. `
      + "Si ha sido un error, contacta con tu encargado; si falta documentación, envía la justificación."
    );
    setNotifEmail(true);
  };

  const enviarNotificacionAusencia = async () => {
    if (!notifAusencia) return;
    setNotifEnviando(true);
    try {
      const { data } = await api.post("/informes/ausencias/notificar", {
        usuario_id: notifAusencia.usuario_id,
        solicitud_id: notifAusencia.id,
        mensaje: notifMensaje,
        enviar_email: notifEmail,
      });
      toast.success(data.email_enviado ? "Notificación enviada (app y correo)" : "Notificación enviada en la app");
      setNotifAusencia(null);
      setNotifMensaje("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNotifEnviando(false);
    }
  };

  const guardarCompensacion = async () => {
    if (!esWorker && !compForm.usuario_id) {
      toast.error("Selecciona un empleado");
      return;
    }
    if (!compForm.motivo.trim() || compForm.motivo.trim().length < 3) {
      toast.error("Indica el motivo de la compensación");
      return;
    }
    const segundos = compCalculo?.segundos_propuestos ?? Math.round(Number(compForm.horas) * 3600);
    if (!segundos || segundos <= 0) {
      toast.error("Indica las horas a compensar");
      return;
    }
    if (compCalculo?.excede) {
      toast.error(`Solo dispones de ${fmtHoras(compCalculo.disponible_segundos)} en este periodo`);
      return;
    }
    setGuardandoComp(true);
    try {
      if (esWorker) {
        await api.post("/compensaciones/solicitar", {
          segundos,
          modalidad: compForm.modalidad,
          fecha_disfrute_inicio: compForm.fecha_disfrute_inicio,
          fecha_disfrute_fin: compForm.fecha_disfrute_fin,
          periodo_desde: compForm.periodo_desde,
          periodo_hasta: compForm.periodo_hasta,
          motivo: compForm.motivo.trim(),
        });
        toast.success("Solicitud enviada a tu encargado");
      } else {
        const payload = {
          usuario_id: Number(compForm.usuario_id),
          segundos,
          periodo_desde: compForm.periodo_desde,
          periodo_hasta: compForm.periodo_hasta,
          fecha_disfrute_inicio: compForm.fecha_disfrute_inicio,
          fecha_disfrute_fin: compForm.fecha_disfrute_fin,
          motivo: compForm.motivo.trim(),
          tipo_compensacion: compForm.tipo_compensacion,
          modalidad: compForm.modalidad,
        };
        if (compForm.tipo_compensacion === "economica" && compForm.importe_euros) {
          payload.importe_centimos = Math.round(Number(compForm.importe_euros) * 100);
        }
        const { data } = await api.post("/compensaciones", payload);
        toast.success(
          data.email_enviado
            ? "Propuesta enviada al trabajador (email y app)"
            : "Propuesta enviada al trabajador para su confirmación"
        );
      }
      setCompOpen(false);
      setCompCalculo(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoComp(false);
    }
  };

  const enviarEmail = async () => {
    setEnviandoEmail(true);
    try {
      const payload = { destinatario: emailDest, formato: emailFormato, ...params };
      if (VISTAS_EXPORT_PROPIO.includes(tipo)) payload.vista = tipo;
      const { data } = await api.post("/informes/enviar-email", payload);
      toast.success(data.mensaje || "Informe enviado");
      setEmailOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviandoEmail(false);
    }
  };

  const tipoActual = TIPOS.find((t) => t.id === tipo);
  const VISTAS_EXPORT_PROPIO = ["fichajes", "incidencias", "ausencias"];

  return (
    <div className="space-y-6" data-testid="informes-page">
      <PageHeader
        icon={ClipboardList}
        title={esWorker ? "Mis informes" : "Listados e informes"}
        subtitle={esWorker
          ? "Consulta tus horas, fichajes, ausencias y compensaciones"
          : "Fichajes, horas, saldos, ausencias e incidencias · exportación PDF/CSV"}
        sectionKey="informes"
        brandGradient={false}
      >
        {tipo === "registro_jornada" ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={() => exportar("csv")} className="gap-1.5 text-white hover:bg-white/20" data-testid="exportar-registro-csv-btn">
              <Download className="h-4 w-4" /> CSV ITSS
            </Button>
            <Button onClick={() => exportar("pdf")} className="gap-1.5 bg-white/20 text-white hover:bg-white/30" data-testid="exportar-registro-pdf-btn">
              <FileText className="h-4 w-4" /> PDF ITSS
            </Button>
          </div>
        ) : tipo === "picados_proyectos" ? (
          <Button variant="ghost" onClick={exportarPicadosProyectos} className="gap-1.5 text-white hover:bg-white/20" disabled={!picadosProyectos?.filas?.length}>
            <Download className="h-4 w-4" /> Excel
          </Button>
        ) : !esWorker ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setEmailOpen(true)} className="gap-1.5 text-white hover:bg-white/20" data-testid="enviar-informe-email-btn">
              <Mail className="h-4 w-4" /> Email
            </Button>
            <Button variant="ghost" onClick={() => exportar("csv")} className="gap-1.5 text-white hover:bg-white/20" data-testid="exportar-informe-btn">
              <Download className="h-4 w-4" /> CSV
            </Button>
            <Button onClick={() => exportar("pdf")} className="gap-1.5 bg-white/20 text-white hover:bg-white/30" data-testid="exportar-pdf-btn">
              <FileText className="h-4 w-4" /> PDF
            </Button>
          </div>
        ) : null}
      </PageHeader>

      {!esWorker && (
        <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-900">
          <strong>{ROL_LABEL[user?.rol] || "Usuario"}:</strong> {ambitoTexto}
        </div>
      )}

      {esWorker ? (
        <div className="flex flex-col gap-2">
          {tiposVisibles.map((t) => {
            const Icon = t.icon;
            const activo = tipo === t.id;
            const secKey = informeSectionKey(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => elegirTipo(t)}
                data-testid={`tipo-informe-${t.id}`}
                className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all ${
                  activo
                    ? "border-indigo-300 bg-white shadow-md ring-2 ring-indigo-200"
                    : "border-zinc-200 bg-white hover:border-indigo-200 hover:shadow-sm"
                }`}
              >
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white"
                  style={sectionGradientStyle(secKey, "to bottom right")}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-zinc-900">{t.label}</p>
                  <p className="mt-0.5 text-xs text-zinc-500">{t.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {tiposVisibles.map((t) => {
            const Icon = t.icon;
            const activo = tipo === t.id;
            const secKey = informeSectionKey(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => elegirTipo(t)}
                data-testid={`tipo-informe-${t.id}`}
                className={`rounded-xl border p-4 text-left transition-all ${
                  activo ? "border-indigo-300 bg-white shadow-md ring-2 ring-indigo-200" : "border-zinc-200 bg-white hover:border-indigo-200 hover:shadow-sm"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white"
                    style={sectionGradientStyle(secKey, "to bottom right")}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900">{t.label}</p>
                    <p className="mt-0.5 text-xs text-zinc-500">{t.desc}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-wide text-zinc-400">
                      {t.perfiles.map((p) => ROL_LABEL[p] || p).join(" · ")}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className={`rounded-2xl border p-4 shadow-sm ${esWorker ? "border-zinc-200 bg-white" : "border-indigo-100 bg-gradient-to-r from-indigo-50/80 to-blue-50/80"}`}>
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          {[["hoy", "Hoy"], ["semana", "Semana"], ["mes", "Mes"], ["mes_anterior", "Mes ant."]].map(([k, label]) => (
            <Button key={k} variant="outline" size="sm" className="rounded-lg border-indigo-100 bg-white" onClick={() => { const [d, h] = rangoRapido(k); setDesde(d); setHasta(h); }} data-testid={`rango-${k}-btn`}>
              {label}
            </Button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9 w-[9.5rem] border-indigo-100 bg-white sm:w-36" data-testid="informe-desde-input" />
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9 w-[9.5rem] border-indigo-100 bg-white sm:w-36" data-testid="informe-hasta-input" />
          {(esAdmin || esManager) && (
            <select className="h-9 min-w-[11rem] max-w-full rounded-md border border-indigo-100 bg-white px-2 text-sm sm:min-w-[12rem]" value={filtroUsuario} onChange={(e) => setFiltroUsuario(e.target.value)}>
              <option value="all">{esManager ? "Todo el equipo" : "Todos los empleados"}</option>
              {usuarios.map((u) => (
                <option key={u.id} value={u.id}>{u.nombre} {u.apellidos}</option>
              ))}
            </select>
          )}
          {esAdmin && (
            <>
              <select className="h-9 min-w-[9rem] max-w-full rounded-md border border-indigo-100 bg-white px-2 text-sm sm:min-w-[10rem]" value={filtroCentro} onChange={(e) => { setFiltroCentro(e.target.value); setFiltroDep("all"); }}>
                <option value="all">Todos los centros</option>
                {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
              <select className="h-9 min-w-[9rem] max-w-full rounded-md border border-indigo-100 bg-white px-2 text-sm sm:min-w-[10rem]" value={filtroDep} onChange={(e) => setFiltroDep(e.target.value)}>
                <option value="all">Todos los deptos</option>
                {depsFiltrados.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
              </select>
            </>
          )}
        </div>

        {tipoActual && (
          <p className="mt-3 text-center text-xs text-indigo-700/70">
            Viendo: <strong>{tipoActual.label}</strong> · {fmtFecha(desde)} — {fmtFecha(hasta)}
          </p>
        )}
      </div>

      <ListadoCabecera
        empresa={cabecera.empresa || resumen?.empresa}
        trabajador={cabecera.trabajador || resumen?.trabajador}
        desde={desde}
        hasta={hasta}
        titulo={tipoActual?.label}
        perfilUrl={esWorker ? "/mi-perfil" : undefined}
      />

      {tipo === "dashboard" && kpis && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard label="Empleados" value={kpis.empleados} sectionKey={kpiSectionKey("empleados")} />
          <KpiCard label="Horas trabajadas" value={fmtHoras(kpis.segundos_trabajados)} sectionKey={kpiSectionKey("horas_trabajadas")} />
          <KpiCard label="Bolsa / saldo" value={`${kpis.saldo_segundos < 0 ? "-" : "+"}${fmtHoras(Math.abs(kpis.saldo_segundos))}`} sectionKey={kpiSectionKey("bolsa")} />
          <KpiCard label="Horas extra" value={fmtHoras(kpis.extra_segundos)} sectionKey={kpiSectionKey("horas_extra")} />
          <KpiCard label="Compensadas" value={fmtHoras(kpis.compensado_segundos || 0)} sectionKey={kpiSectionKey("compensadas")} />
          <KpiCard
            label="Extra pendiente"
            value={fmtHoras(kpis.extra_pendiente_segundos || 0)}
            sectionKey={kpiSectionKey("extra_pendiente")}
            onClick={(esAdmin || esManager) ? abrirExtraPendientes : undefined}
            title="Ver empleados con horas extra pendientes"
          />
          <KpiCard label="Días ausencia" value={kpis.dias_ausencia} sectionKey={kpiSectionKey("dias_ausencia")} onClick={() => abrirDetalleAusencias()} />
          <KpiCard label="Fichajes manuales" value={kpis.fichajes_manuales} sectionKey={kpiSectionKey("fichajes_manuales")} onClick={() => abrirDetalleManuales()} />
          <KpiCard label="Sesiones abiertas" value={kpis.sesiones_abiertas} sectionKey={kpiSectionKey("sesiones_abiertas")} />
        </div>
      )}

      {tipo === "balance" && esWorker && resumen?.usuarios?.[0] && (
        <BalanceHorasCards
          usuario={resumen.usuarios[0]}
          periodo={`${fmtFecha(desde)} – ${fmtFecha(hasta)}`}
        />
      )}

      {(tipo === "balance" || tipo === "dashboard" || tipo === "compensaciones") && resumen && !(tipo === "balance" && esWorker) && (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white shadow-sm">
          <table className="w-full text-sm" data-testid="informe-tabla">
            <thead>
              <tr className="border-b border-indigo-100 bg-gradient-to-r from-indigo-50 to-blue-50 text-left text-[11px] font-semibold uppercase tracking-wide text-indigo-800">
                <th className="px-4 py-3">Empleado</th>
                <th className="px-4 py-3">Departamento</th>
                <th className="px-4 py-3">Días</th>
                <th className="px-4 py-3">Horas</th>
                <th className="px-4 py-3">Previstas</th>
                <th className="px-4 py-3">Bolsa</th>
                <th className="px-4 py-3">Extra</th>
                <th className="px-4 py-3">Compensado</th>
                <th className="px-4 py-3">Pendiente</th>
                <th className="px-4 py-3">Manuales</th>
                {(esAdmin || esManager) && (tipo === "balance" || tipo === "compensaciones") && (
                  <th className="px-4 py-3 text-right">Acción</th>
                )}
              </tr>
            </thead>
            <tbody>
              {resumen.usuarios.map((u) => (
                <tr key={u.usuario_id} className="border-b border-zinc-100 hover:bg-indigo-50/30">
                  <td className="px-4 py-2.5 font-medium">
                    {tipo === "dashboard" ? (
                      <button
                        type="button"
                        className="text-left font-medium text-indigo-700 hover:underline"
                        onClick={() => abrirDetalleFichajes(u.usuario_id, `Picados — ${u.usuario}`)}
                      >
                        {u.usuario}
                      </button>
                    ) : (
                      u.usuario
                    )}
                    {u.sesion_abierta && <span className="ml-2 inline-block h-1.5 w-1.5 rounded-full bg-amber-500" title="Sesión abierta" />}
                  </td>
                  <td className="px-4 py-2.5 text-zinc-500">{u.departamento || "—"}</td>
                  <td className="px-4 py-2.5">{u.dias_trabajados}</td>
                  <td className="px-4 py-2.5 font-medium">{fmtHoras(u.segundos)}</td>
                  <td className="px-4 py-2.5 text-zinc-500">{fmtHoras(u.segundos_previstos)}</td>
                  <td className={`px-4 py-2.5 font-medium ${u.saldo_segundos < 0 ? "text-red-600" : "text-green-600"}`}>
                    {u.saldo_segundos < 0 ? "-" : "+"}{fmtHoras(Math.abs(u.saldo_segundos))}
                  </td>
                  <td className="px-4 py-2.5">{fmtHoras(u.extra_segundos || 0)}</td>
                  <td className="px-4 py-2.5 text-violet-700">{fmtHoras(u.compensado_segundos || 0)}</td>
                  <td className={`px-4 py-2.5 font-medium ${(u.extra_pendiente_segundos || 0) > 0 ? "text-fuchsia-700" : "text-zinc-400"}`}>
                    {(esAdmin || esManager) && (u.extra_pendiente_segundos || 0) > 0 && tipo === "dashboard" ? (
                      <button
                        type="button"
                        className="font-medium text-fuchsia-700 hover:underline"
                        onClick={() => abrirCompensacion(u)}
                      >
                        {fmtHoras(u.extra_pendiente_segundos || 0)}
                      </button>
                    ) : (
                      fmtHoras(u.extra_pendiente_segundos || 0)
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {u.manuales > 0 ? (
                      tipo === "dashboard" ? (
                        <button
                          type="button"
                          className="font-medium text-amber-600 hover:underline"
                          onClick={() => abrirDetalleManuales(u.usuario_id, u.usuario)}
                        >
                          {u.manuales}
                        </button>
                      ) : (
                        <span className="text-amber-600">{u.manuales}</span>
                      )
                    ) : "—"}
                  </td>
                  {(esAdmin || esManager) && (tipo === "balance" || tipo === "compensaciones") && (
                    <td className="px-4 py-2.5 text-right">
                      {(u.extra_pendiente_segundos || 0) > 0 && (
                        <Button size="sm" variant="outline" className="h-8 border-violet-200 text-violet-700" onClick={() => abrirCompensacion(u)}>
                          Compensar
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tipo === "registro_jornada" && registroJornada && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <p className="font-medium text-slate-900">Registro de jornada — {registroJornada.legal?.norma || "Art. 34.9 ET"}</p>
            <p className="mt-1">
              Documento con hora de inicio y fin de cada jornada, pausas y fichajes manuales.
              Conservación mínima: <strong>{registroJornada.legal?.conservacion || "4 años"}</strong>.
              Acceso: {registroJornada.legal?.acceso || "empresa, trabajadores, representantes e ITSS"}.
            </p>
            {!registroJornada.empleados?.some((e) => e.dni_nie) && (
              <p className="mt-2 text-amber-800">
                Aviso: complete el DNI/NIE de los empleados en Empleados para un registro plenamente identificable ante inspección.
              </p>
            )}
          </div>
          {registroJornada.empleados?.length === 0 ? (
            <div className="rounded-2xl border border-zinc-200 bg-white p-8 text-center text-zinc-500">
              No hay empleados en el ámbito seleccionado.
            </div>
          ) : (
            registroJornada.empleados.map((emp) => (
              <div key={emp.usuario_id} className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white shadow-sm">
                <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-zinc-50 px-4 py-3">
                  <p className="font-medium text-slate-900">{emp.nombre}</p>
                  <p className="text-xs text-slate-500">
                    {[emp.dni_nie && `DNI/NIE: ${emp.dni_nie}`, emp.departamento, emp.centro].filter(Boolean).join(" · ")}
                  </p>
                </div>
                {emp.registros?.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-zinc-500">Sin registros en el periodo.</p>
                ) : (
                  <table className="w-full text-sm" data-testid={`registro-jornada-${emp.usuario_id}`}>
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                        <th className="px-4 py-2.5">Fecha</th>
                        <th className="px-4 py-2.5">Inicio</th>
                        <th className="px-4 py-2.5">Fin</th>
                        <th className="px-4 py-2.5">Pausas</th>
                        <th className="px-4 py-2.5">Horas</th>
                        <th className="px-4 py-2.5">Manual</th>
                        <th className="px-4 py-2.5">Ausencia</th>
                        <th className="px-4 py-2.5">Observaciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emp.registros.map((r) => (
                        <tr key={r.fecha} className="border-b border-zinc-100 hover:bg-slate-50/50">
                          <td className="px-4 py-2">{fmtFecha(r.fecha)}</td>
                          <td className="px-4 py-2 font-medium">{r.inicio || "—"}</td>
                          <td className="px-4 py-2 font-medium">
                            {r.fin || (r.sesion_abierta ? <span className="text-amber-600">Abierta</span> : "—")}
                          </td>
                          <td className="px-4 py-2 text-zinc-600">{r.pausas_texto || "—"}</td>
                          <td className="px-4 py-2">{r.horas || "00:00"}</td>
                          <td className="px-4 py-2">{r.manual ? <span className="text-amber-600">Sí</span> : "No"}</td>
                          <td className="px-4 py-2 text-rose-700">{r.ausencia || "—"}</td>
                          <td className="px-4 py-2 text-xs text-zinc-500">{r.observaciones || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {tipo === "fichajes" && (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white shadow-sm">
          <table className="w-full text-sm" data-testid="fichajes-listado-tabla">
            <thead>
              <tr className="border-b border-sky-100 bg-gradient-to-r from-sky-50 to-blue-50 text-left text-[11px] font-semibold uppercase text-sky-800">
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Empleado</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Manual</th>
                <th className="px-4 py-3">GPS</th>
              </tr>
            </thead>
            <tbody>
              {fichajes.map((f) => (
                <tr key={f.id} className="border-b border-zinc-100 hover:bg-sky-50/30">
                  <td className="px-4 py-2.5">{fmtFecha(f.timestamp)} {fmtHora(f.timestamp)}</td>
                  <td className="px-4 py-2.5">
                    <p className="font-medium">{f.usuario}</p>
                    <p className="text-xs text-zinc-400">{f.departamento || "—"}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline" className={f.tipo === "entrada" ? "border-green-200 bg-green-50 text-green-700" : "border-zinc-200 bg-zinc-50"}>
                      {f.tipo === "entrada" ? "Entrada" : "Salida"}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5">{f.es_manual ? <span className="text-amber-600" title={f.justificacion}>Sí</span> : "—"}</td>
                  <td className="px-4 py-2.5">
                    {f.latitud != null ? (
                      <button type="button" onClick={() => setMapaFichaje(f)} className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline">
                        <MapPin className="h-3.5 w-3.5" /> Ver mapa
                      </button>
                    ) : (
                      <span className="text-zinc-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {fichajes.length === 0 && (
                <tr><td colSpan="5" className="p-8 text-center text-zinc-400">Sin fichajes en el periodo.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tipo === "picados_proyectos" && (
        <div className="space-y-3">
          {picadosProyectos && (
            <p className="text-sm text-zinc-600">
              {picadosProyectos.total_sesiones} sesiones · Total: <strong>{picadosProyectos.total_horas}</strong>
            </p>
          )}
          <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white shadow-sm">
            <table className="w-full text-sm" data-testid="picados-proyectos-tabla">
              <thead>
                <tr className="border-b border-violet-100 bg-gradient-to-r from-violet-50 to-purple-50 text-left text-[11px] font-semibold uppercase text-violet-900">
                  {picadosProyectos?.incluir_trabajador && <th className="px-4 py-3">Trabajador</th>}
                  <th className="px-4 py-3">Código</th>
                  <th className="px-4 py-3">Proyecto</th>
                  <th className="px-4 py-3">Día</th>
                  <th className="px-4 py-3">Fecha</th>
                  <th className="px-4 py-3">Entrada</th>
                  <th className="px-4 py-3">Salida</th>
                  <th className="px-4 py-3">Tiempo</th>
                </tr>
              </thead>
              <tbody>
                {(picadosProyectos?.filas || []).map((r, i) => (
                  <tr key={i} className="border-b border-zinc-100 hover:bg-violet-50/30">
                    {picadosProyectos?.incluir_trabajador && <td className="px-4 py-2.5 font-medium">{r.usuario}</td>}
                    <td className="px-4 py-2.5 font-mono text-xs">{r.proyecto_codigo}</td>
                    <td className="px-4 py-2.5">{r.proyecto_nombre}</td>
                    <td className="px-4 py-2.5">{r.dia_semana}</td>
                    <td className="px-4 py-2.5">{r.fecha_fmt}</td>
                    <td className="px-4 py-2.5">{r.entrada_hora}</td>
                    <td className="px-4 py-2.5">{r.salida_hora}</td>
                    <td className="px-4 py-2.5">{r.duracion}</td>
                  </tr>
                ))}
                {(!picadosProyectos?.filas || picadosProyectos.filas.length === 0) && (
                  <tr>
                    <td colSpan={picadosProyectos?.incluir_trabajador ? 8 : 7} className="p-8 text-center text-zinc-400">
                      Sin picados en proyectos en el periodo seleccionado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tipo === "incidencias" && (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-amber-100 bg-gradient-to-r from-amber-50 to-orange-50 text-left text-[11px] font-semibold uppercase text-amber-900">
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Empleado</th>
                <th className="px-4 py-3">Departamento</th>
                <th className="px-4 py-3">Detalle</th>
                <th className="px-4 py-3 text-right">Aviso</th>
              </tr>
            </thead>
            <tbody>
              {incidencias.map((inc, i) => (
                <tr key={i} className="border-b border-zinc-100 hover:bg-amber-50/30">
                  <td className="px-4 py-2.5"><Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">{inc.label}</Badge></td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">{inc.fecha ? fmtFecha(inc.fecha) : "—"}</td>
                  <td className="px-4 py-2.5 font-medium">{inc.usuario}</td>
                  <td className="px-4 py-2.5 text-zinc-500">{inc.departamento || "—"}</td>
                  <td className="px-4 py-2.5 text-zinc-600">{inc.detalle}</td>
                  <td className="px-4 py-2.5 text-right">
                    {inc.usuario_id && (inc.tipo === "falta_entrada" || inc.tipo === "falta_salida") && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 gap-1 text-amber-700"
                        onClick={async () => {
                          try {
                            await api.post("/fichajes/recordatorios", {
                              usuario_ids: [inc.usuario_id],
                              mensaje: inc.detalle,
                              enviar_email: true,
                            });
                            toast.success(`Aviso enviado a ${inc.usuario}`);
                          } catch (e) {
                            toast.error(formatApiError(e));
                          }
                        }}
                      >
                        <Bell className="h-3.5 w-3.5" />
                        Avisar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {incidencias.length === 0 && (
                <tr><td colSpan="6" className="p-8 text-center text-zinc-400">Sin incidencias en el periodo.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tipo === "ausencias" && (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rose-100 bg-gradient-to-r from-rose-50 to-pink-50 text-left text-[11px] font-semibold uppercase text-rose-900">
                <th className="px-4 py-3">Empleado</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Alcance / tramo</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Desde</th>
                <th className="px-4 py-3">Hasta</th>
                <th className="px-4 py-3">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {ausencias.map((a) => {
                const st = badgeEstadoAusencia(a);
                return (
                <tr key={a.id} className="border-b border-zinc-100 hover:bg-rose-50/30">
                  <td className="px-4 py-2.5">
                    <p className="font-medium">{a.usuario}</p>
                    <p className="text-xs text-zinc-400">{[a.centro, a.departamento].filter(Boolean).join(" · ")}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge
                      variant="outline"
                      className={a.tipo === "ausencia_justificada" ? "border-violet-200 bg-violet-50 text-violet-800" : ""}
                    >
                      {a.tipo_label}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5"><AlcanceAusencia a={a} /></td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline" className={st.className}>{st.label}</Badge>
                  </td>
                  <td className="px-4 py-2.5">{fmtFecha(a.fecha_inicio)}</td>
                  <td className="px-4 py-2.5">{fmtFecha(a.fecha_fin)}</td>
                  <td className="px-4 py-2.5 text-zinc-500">{a.motivo || "—"}</td>
                </tr>
              );})}
              {ausencias.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-zinc-400">Sin ausencias en el periodo.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tipo === "compensaciones" && (
        <div className="space-y-4">
          {(esAdmin || esManager) && pendRevision.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
              <p className="mb-3 text-sm font-medium text-amber-900">Solicitudes del trabajador pendientes de aprobación</p>
              <div className="space-y-2">
                {pendRevision.map((c) => (
                  <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-100 bg-white px-4 py-3 text-sm">
                    <div>
                      <p className="font-medium">{c.usuario} · {fmtHoras(c.segundos)}</p>
                      <p className="text-xs text-zinc-500">{c.motivo}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => revisarCompensacion(c, true)}>Aprobar</Button>
                      <Button size="sm" variant="outline" onClick={() => revisarCompensacion(c, false)}>Rechazar</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(esAdmin || esManager) && (
            <div className="flex justify-end">
              <Button onClick={() => abrirCompensacion()} className="bg-violet-600 hover:bg-violet-700" data-testid="nueva-compensacion-btn">
                Proponer compensación
              </Button>
            </div>
          )}
          {esWorker && (
            <div className="flex justify-end">
              <Button onClick={() => abrirCompensacion()} className="bg-violet-600 hover:bg-violet-700">
                Solicitar compensación
              </Button>
            </div>
          )}
          <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white shadow-sm">
            <div className="border-b border-violet-100 bg-violet-50 px-4 py-3 text-sm text-violet-900">
              Historial de compensaciones. Las propuestas del encargado requieren confirmación del trabajador.
            </div>
            <table className="w-full text-sm" data-testid="compensaciones-tabla">
              <thead>
                <tr className="border-b border-violet-100 bg-gradient-to-r from-violet-50 to-purple-50 text-left text-[11px] font-semibold uppercase text-violet-900">
                  <th className="px-4 py-3">Empleado</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Horas</th>
                  <th className="px-4 py-3">Disfrute</th>
                  <th className="px-4 py-3">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {compensaciones.map((c) => (
                  <tr key={c.id} className="border-b border-zinc-100 hover:bg-violet-50/30">
                    <td className="px-4 py-2.5 font-medium">{c.usuario}</td>
                    <td className="px-4 py-2.5 text-zinc-600">{c.tipo_compensacion === "economica" ? "Económica" : "Tiempo libre"}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className={
                        c.estado === "aprobada" ? "border-green-200 text-green-700" :
                        c.estado === "rechazada" ? "border-red-200 text-red-700" :
                        "border-amber-200 text-amber-700"
                      }>
                        {c.estado === "pendiente_encargado" ? "Pend. encargado" :
                         c.estado === "pendiente_trabajador" ? "Pend. trabajador" :
                         c.estado === "aprobada" ? "Aprobada" : "Rechazada"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5">{fmtHoras(c.segundos)}</td>
                    <td className="px-4 py-2.5">{fmtFecha(c.fecha_disfrute_inicio)} — {fmtFecha(c.fecha_disfrute_fin)}</td>
                    <td className="px-4 py-2.5 text-zinc-600">{c.motivo || "—"}</td>
                  </tr>
                ))}
                {compensaciones.length === 0 && (
                  <tr><td colSpan="6" className="p-8 text-center text-zinc-400">Sin compensaciones en el periodo.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <MapaFichajeDialog fichaje={mapaFichaje} onClose={() => setMapaFichaje(null)} />
      <FichajeTrazabilidadDialog fichaje={trazabilidadFichaje} onClose={() => setTrazabilidadFichaje(null)} />

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar listado por email</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-500">
            Se adjuntará <strong>{tipoActual?.label || "el informe"}</strong> del periodo {fmtFecha(desde)} — {fmtFecha(hasta)} con los filtros actuales.
            {esWorker && " Solo puedes enviarlo a tu correo."}
          </p>
          <div className="space-y-3">
            <Input
              type="email"
              value={emailDest}
              onChange={(e) => setEmailDest(e.target.value)}
              placeholder="correo@empresa.es"
              disabled={esWorker}
              data-testid="informe-email-input"
            />
            <select
              className="h-9 w-full rounded-md border border-zinc-200 px-2 text-sm"
              value={emailFormato}
              onChange={(e) => setEmailFormato(e.target.value)}
            >
              <option value="csv">CSV</option>
              <option value="pdf">PDF</option>
            </select>
            <Button onClick={enviarEmail} disabled={enviandoEmail || !emailDest} className="w-full" data-testid="informe-email-enviar-btn">
              {enviandoEmail ? "Enviando…" : "Enviar informe"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={detalleOpen} onOpenChange={(open) => { if (!open) cerrarDetalle(); else setDetalleOpen(true); }}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detalleTitulo}</DialogTitle>
          </DialogHeader>

          {detalleLoading ? (
            <p className="py-8 text-center text-sm text-zinc-400">Cargando…</p>
          ) : detalleModo === "fichajes" || detalleModo === "manuales" ? (
            <div className="overflow-x-auto rounded-lg border border-zinc-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-zinc-50 text-left text-[11px] font-semibold uppercase text-zinc-600">
                    <th className="px-3 py-2">Fecha / hora</th>
                    {!params.usuario_id && detalleModo === "manuales" && <th className="px-3 py-2">Empleado</th>}
                    <th className="px-3 py-2">Tipo</th>
                    {detalleModo === "manuales" && <th className="px-3 py-2">Justificación</th>}
                    <th className="px-3 py-2">Registrado por</th>
                    <th className="px-3 py-2">GPS</th>
                    <th className="px-3 py-2 text-right">Trazabilidad</th>
                  </tr>
                </thead>
                <tbody>
                  {detalleItems.map((f) => (
                    <tr key={f.id} className="border-b border-zinc-100">
                      <td className="px-3 py-2">{fmtFecha(f.timestamp)} {fmtHora(f.timestamp)}</td>
                      {!params.usuario_id && detalleModo === "manuales" && (
                        <td className="px-3 py-2">{f.usuario}</td>
                      )}
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={f.tipo === "entrada" ? "border-green-200 bg-green-50 text-green-700" : "border-zinc-200 bg-zinc-50"}>
                          {f.tipo === "entrada" ? "Entrada" : f.tipo === "salida" ? "Salida" : f.tipo}
                        </Badge>
                      </td>
                      {detalleModo === "manuales" && (
                        <td className="px-3 py-2 text-zinc-600">{f.justificacion || "—"}</td>
                      )}
                      <td className="px-3 py-2 text-zinc-500">{resumenRegistradoFichaje(f)}</td>
                      <td className="px-3 py-2">
                        {f.latitud != null ? (
                          <button type="button" onClick={() => setMapaFichaje(f)} className="inline-flex items-center gap-1 text-indigo-600 hover:underline">
                            <MapPin className="h-3.5 w-3.5" /> Mapa
                          </button>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 gap-1 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-800"
                          onClick={() => setTrazabilidadFichaje(f)}
                          data-testid={`trazabilidad-fichaje-${f.id}`}
                        >
                          <History className="h-3.5 w-3.5" />
                          Ver
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {detalleItems.length === 0 && (
                    <tr>
                      <td colSpan={detalleModo === "manuales" ? 7 : 5} className="p-6 text-center text-zinc-400">
                        Sin registros en el periodo.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : detalleModo === "ausencias" ? (
            <>
              <div className="overflow-x-auto rounded-lg border border-zinc-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-rose-50 text-left text-[11px] font-semibold uppercase text-rose-900">
                      <th className="px-3 py-2">Empleado</th>
                      <th className="px-3 py-2">Tipo</th>
                      <th className="px-3 py-2">Alcance / tramo</th>
                      <th className="px-3 py-2">Estado</th>
                      <th className="px-3 py-2">Desde</th>
                      <th className="px-3 py-2">Hasta</th>
                      <th className="px-3 py-2">Motivo</th>
                      {(esAdmin || esManager) && <th className="px-3 py-2 text-right">Acción</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {detalleItems.map((a) => {
                      const st = badgeEstadoAusencia(a);
                      return (
                      <tr key={a.id} className="border-b border-zinc-100">
                        <td className="px-3 py-2 font-medium">{a.usuario}</td>
                        <td className="px-3 py-2">{a.tipo_label}</td>
                        <td className="px-3 py-2"><AlcanceAusencia a={a} /></td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={st.className}>{st.label}</Badge>
                        </td>
                        <td className="px-3 py-2">{fmtFecha(a.fecha_inicio)}</td>
                        <td className="px-3 py-2">{fmtFecha(a.fecha_fin)}</td>
                        <td className="px-3 py-2 text-zinc-600">{a.motivo || "—"}</td>
                        {(esAdmin || esManager) && (
                          <td className="px-3 py-2 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 border-rose-200 text-rose-700"
                              onClick={() => abrirNotificarAusencia(a)}
                            >
                              <Bell className="mr-1 h-3.5 w-3.5" />
                              Notificar
                            </Button>
                          </td>
                        )}
                      </tr>
                    );})}
                    {detalleItems.length === 0 && (
                      <tr>
                        <td colSpan={(esAdmin || esManager) ? 8 : 7} className="p-6 text-center text-zinc-400">
                          Sin ausencias en el periodo.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {notifAusencia && (
                <div className="mt-4 space-y-3 rounded-lg border border-rose-200 bg-rose-50/50 p-4">
                  <p className="text-sm font-medium text-rose-900">
                    Notificar a {notifAusencia.usuario}
                  </p>
                  <p className="text-xs text-zinc-500">
                    Se enviará un aviso in-app al trabajador para que revise si la ausencia es un error o necesita justificación.
                  </p>
                  <Textarea
                    value={notifMensaje}
                    onChange={(e) => setNotifMensaje(e.target.value)}
                    rows={4}
                    data-testid="notif-ausencia-mensaje"
                  />
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-600">
                    <Checkbox checked={notifEmail} onCheckedChange={(v) => setNotifEmail(v === true)} />
                    Enviar también por correo electrónico
                  </label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-rose-600 hover:bg-rose-700"
                      onClick={enviarNotificacionAusencia}
                      disabled={notifEnviando}
                      data-testid="notif-ausencia-enviar"
                    >
                      <Mail className="mr-1 h-3.5 w-3.5" />
                      {notifEnviando ? "Enviando…" : "Enviar notificación"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setNotifAusencia(null); setNotifMensaje(""); }}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={cerrarDetalle}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={compOpen} onOpenChange={(o) => { setCompOpen(o); if (!o) setCompCalculo(null); }}>
        <DialogContent className="max-w-md" data-testid="compensacion-dialog">
          <DialogHeader>
            <DialogTitle>{esWorker ? "Solicitar compensación" : "Compensar horas extra"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-zinc-500">
              {esWorker
                ? "Indica cómo quieres compensar tus horas extra. El encargado deberá aprobarlo."
                : "Propón compensación por tiempo libre o económica. El trabajador deberá confirmarla en Mis solicitudes."}
            </p>
            {(esAdmin || esManager) && (
              <div className="space-y-1.5">
                <Label>Empleado</Label>
                <select
                  className="h-9 w-full rounded-md border border-zinc-200 px-2"
                  value={compForm.usuario_id}
                  onChange={(e) => setCompForm({ ...compForm, usuario_id: e.target.value })}
                >
                  <option value="">Selecciona…</option>
                  {usuarios.filter((u) => u.activo && u.rol === "worker").map((u) => (
                    <option key={u.id} value={u.id}>{u.nombre} {u.apellidos}</option>
                  ))}
                </select>
              </div>
            )}
            {(esAdmin || esManager) && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label>Tipo</Label>
                  <select
                    className="h-9 w-full rounded-md border border-zinc-200 px-2"
                    value={compForm.tipo_compensacion}
                    onChange={(e) => setCompForm({ ...compForm, tipo_compensacion: e.target.value })}
                  >
                    <option value="tiempo_libre">Tiempo libre</option>
                    <option value="economica">Económica</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Modalidad</Label>
                  <select
                    className="h-9 w-full rounded-md border border-zinc-200 px-2"
                    value={compForm.modalidad}
                    onChange={(e) => setCompForm({ ...compForm, modalidad: e.target.value })}
                  >
                    <option value="horas">Horas</option>
                    <option value="dia">Día completo</option>
                    <option value="periodo">Periodo</option>
                  </select>
                </div>
              </div>
            )}
            {esWorker && (
              <div className="space-y-1.5">
                <Label>Modalidad</Label>
                <select
                  className="h-9 w-full rounded-md border border-zinc-200 px-2"
                  value={compForm.modalidad}
                  onChange={(e) => setCompForm({ ...compForm, modalidad: e.target.value })}
                >
                  <option value="horas">Horas sueltas</option>
                  <option value="dia">Día completo</option>
                  <option value="periodo">Periodo</option>
                </select>
              </div>
            )}
            {!esWorker && compForm.tipo_compensacion === "economica" && (
              <div className="space-y-1.5">
                <Label>Importe (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={compForm.importe_euros}
                  onChange={(e) => setCompForm({ ...compForm, importe_euros: e.target.value })}
                />
              </div>
            )}
            {compForm.modalidad === "horas" && (
              <div className="space-y-1.5">
                <Label>Horas a compensar</Label>
                <Input
                  type="number"
                  min="0.25"
                  step="0.25"
                  value={compForm.horas}
                  onChange={(e) => setCompForm({ ...compForm, horas: e.target.value })}
                  data-testid="compensacion-horas-input"
                />
              </div>
            )}
            {compForm.modalidad !== "horas" && (
              <p className="text-xs text-violet-700">
                Las horas se calculan según el horario del trabajador en cada día laborable del disfrute.
              </p>
            )}
            <CompensacionSaldoPanel
              modalidad={compForm.modalidad}
              horas={compForm.horas}
              fechaDisfruteInicio={compForm.fecha_disfrute_inicio}
              fechaDisfruteFin={compForm.fecha_disfrute_fin}
              usuarioId={esWorker ? user?.id : (compForm.usuario_id ? Number(compForm.usuario_id) : null)}
              periodoDesde={compForm.periodo_desde}
              periodoHasta={compForm.periodo_hasta}
              onCalculo={setCompCalculo}
            />
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>Periodo ref. desde</Label>
                <Input type="date" value={compForm.periodo_desde} onChange={(e) => setCompForm({ ...compForm, periodo_desde: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Periodo ref. hasta</Label>
                <Input type="date" value={compForm.periodo_hasta} onChange={(e) => setCompForm({ ...compForm, periodo_hasta: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>Disfrute desde</Label>
                <Input type="date" value={compForm.fecha_disfrute_inicio} onChange={(e) => setCompForm({ ...compForm, fecha_disfrute_inicio: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Disfrute hasta</Label>
                <Input type="date" value={compForm.fecha_disfrute_fin} onChange={(e) => setCompForm({ ...compForm, fecha_disfrute_fin: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Motivo</Label>
              <Textarea value={compForm.motivo} onChange={(e) => setCompForm({ ...compForm, motivo: e.target.value })} data-testid="compensacion-motivo-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={guardarCompensacion} disabled={guardandoComp || compCalculo?.excede} className="bg-violet-600 hover:bg-violet-700" data-testid="compensacion-guardar-btn">
              {guardandoComp ? "Enviando…" : esWorker ? "Enviar a mi encargado" : "Enviar propuesta al trabajador"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={extraPendOpen} onOpenChange={setExtraPendOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Horas extra pendientes de compensar</DialogTitle>
          </DialogHeader>
          {extraPendLoading ? (
            <p className="py-8 text-center text-zinc-400">Cargando…</p>
          ) : (
            <div className="space-y-2">
              {extraPendItems.map((u) => (
                <div key={u.usuario_id} className="flex items-center justify-between rounded-xl border border-fuchsia-100 bg-fuchsia-50/30 px-4 py-3">
                  <div>
                    <p className="font-medium text-zinc-900">{u.usuario}</p>
                    <p className="text-xs text-zinc-500">{u.departamento || "—"}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-fuchsia-700">{fmtHoras(u.extra_pendiente_segundos)}</span>
                    <Button size="sm" variant="outline" className="border-violet-200 text-violet-700" onClick={() => { setExtraPendOpen(false); abrirCompensacion(u); }}>
                      Compensar
                    </Button>
                  </div>
                </div>
              ))}
              {extraPendItems.length === 0 && (
                <p className="py-8 text-center text-zinc-400">No hay horas extra pendientes en este periodo.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtraPendOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
